import React, { useState, useEffect, useLayoutEffect, useRef } from "react";

// Onboarding coach mark — overlay that dims the screen, cuts out a
// "spotlight" around a target DOM element, and shows a tooltip card
// next to it. Sequential walkthrough is composed via CoachMarkTour.
//
// Why 4-div spotlight (not SVG mask): with 4 fixed-position divs
// surrounding the target rect, the target element shows through
// untouched. The user can still tap the highlighted button — useful
// for "tap here to continue" interactions. SVG masks block clicks.
//
// Design rules applied (ui-ux-pro-max):
//   §1 accessibility — role=dialog + aria-labelledby, focus trap on
//     primary button, ESC to skip, prefers-reduced-motion respected,
//     decorative SVGs aria-hidden.
//   §2 touch — Next button 44pt+; tooltip card never overflows
//     viewport (mobile-first 430px-aware).
//   §4 style — refined-minimal, single accent color per step.
//   §7 animation — soft 200ms fade-in; pulsing border on the
//     spotlighted target tells the eye where to look.
//
// Props:
//   target:    CSS selector (e.g. '[data-coach="nav-payments"]')
//   title:     short noun phrase
//   body:      1-2 sentence description
//   placement: 'top' | 'bottom' | 'auto'  (default auto)
//   stepLabel: e.g. "2 of 5" (rendered above title)
//   isFirst, isLast: nav state for button label
//   onNext, onBack, onSkip
//   h:         theme tokens
//   accent:    color override (per-step branding)
export default function CoachMark({
  target,
  title,
  body,
  placement = "auto",
  stepLabel,
  isFirst = false,
  isLast = false,
  onNext,
  onBack,
  onSkip,
  h = {},
  accent,
}) {
  const [rect, setRect] = useState(null);
  const [tries, setTries] = useState(0);
  // True once we've exhausted the poll loop without finding the
  // target — used to show "Skip step" instead of "Loading…" so the
  // user always has an escape route.
  const [exhausted, setExhausted] = useState(false);
  const tooltipRef = useRef(null);
  const primaryBtnRef = useRef(null);

  // Poll for the target — components may mount async (route changes,
  // lazy loaders). Give it up to 3s. If still not found, skip the
  // step (handled by parent via onNext if rect is null after timeout).
  useLayoutEffect(() => {
    let cancelled = false;

    const findTarget = () => {
      if (cancelled) return;
      const el = document.querySelector(target);
      if (el) {
        // Scroll into view if needed — coach marks pointing at
        // off-screen elements would otherwise look broken.
        const r = el.getBoundingClientRect();
        const offscreen = r.bottom < 0 || r.top > window.innerHeight ||
                          r.right < 0 || r.left > window.innerWidth;
        if (offscreen) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          // Wait for scroll to settle, then re-measure.
          setTimeout(() => {
            if (!cancelled) setRect(el.getBoundingClientRect());
          }, 380);
        } else {
          setRect(r);
        }
      } else if (tries < 12) {
        setTimeout(() => { if (!cancelled) setTries(t => t + 1); }, 250);
      } else if (!cancelled) {
        // Poll exhausted (12 × 250ms = 3s) and target still missing.
        // Auto-skip to the next step so the user is never stuck.
        // The exhausted flag flips the UI to "Skip step →" first so
        // they see SOMETHING before we move on, then we call onNext
        // after a short pause.
        setExhausted(true);
        setTimeout(() => {
          if (!cancelled) {
            try { onNext?.(); } catch (_) { /* ignore */ }
          }
        }, 600);
      }
    };

    findTarget();

    // Re-measure on viewport changes — orientation flip, soft
    // keyboard appearing, etc.
    const onResize = () => {
      const el = document.querySelector(target);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, tries]);

  // Auto-focus primary button so Enter advances.
  useEffect(() => {
    if (rect) {
      const id = requestAnimationFrame(() => {
        try { primaryBtnRef.current?.focus(); } catch (_) {}
      });
      return () => cancelAnimationFrame(id);
    }
  }, [rect]);

  // Keyboard: ESC skip, ←/→ nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onSkip?.(); }
      else if (e.key === "ArrowRight" || e.key === "Enter") {
        // Enter would also fire on the focused button — but we
        // call onNext explicitly so the parent state machine
        // advances even if focus shifted.
        if (e.target?.tagName !== "BUTTON") { e.preventDefault(); onNext?.(); }
      }
      else if (e.key === "ArrowLeft") { e.preventDefault(); onBack?.(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNext, onBack, onSkip]);

  const ac = accent || h.ac || "#3b82f6";
  const bg = "rgba(8, 10, 22, 0.62)";
  const cardBg = h.cd || "#ffffff";
  const tx = h.tx || "#111114";
  const sc = h.sc || "#8e8e93";
  const bd = h.bd || "rgba(0,0,0,0.08)";

  // When target isn't found yet, render the dimmer + a centered
  // "Loading…" card. Always include a Skip-step button so the user
  // can never be stuck if a target is genuinely missing (data-coach
  // attribute typo, lazy-mounted component, dynamic route, etc).
  if (!rect) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Onboarding step loading"
        style={{
          position: "fixed", inset: 0, zIndex: 9998,
          background: bg, backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
        }}
      >
        <div style={{
          background: cardBg, borderRadius: 16, padding: "18px 22px 16px",
          boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
          border: `1px solid ${bd}`,
          minWidth: 240, maxWidth: 320,
          textAlign: "center",
        }}>
          <div style={{ color: tx, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            {exhausted ? "Couldn't find this step" : "Loading next step…"}
          </div>
          <div style={{ color: sc, fontSize: 11, lineHeight: 1.4, marginBottom: 12 }}>
            {exhausted
              ? "The screen for this tip isn't visible right now. Skipping ahead."
              : "Looking for the right spot on this screen…"}
          </div>
          <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
            <button
              onClick={onSkip}
              style={{
                padding: "8px 14px",
                borderRadius: 10,
                border: `1px solid ${bd}`,
                background: "transparent",
                color: sc,
                fontSize: 11, fontWeight: 600,
                cursor: "pointer",
                minHeight: 36,
              }}
            >
              End tour
            </button>
            <button
              onClick={onNext}
              style={{
                padding: "8px 14px",
                borderRadius: 10,
                border: "none",
                background: ac,
                color: "#fff",
                fontSize: 11, fontWeight: 700,
                cursor: "pointer",
                boxShadow: `0 3px 10px ${ac}40`,
                minHeight: 36,
              }}
            >
              Skip this step →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Spotlight rectangle ────────────────────────────────
  // Pad slightly so the target isn't flush with the cutout edge.
  const padding = 8;
  const r = {
    top: Math.max(0, rect.top - padding),
    left: Math.max(0, rect.left - padding),
    right: Math.min(window.innerWidth, rect.right + padding),
    bottom: Math.min(window.innerHeight, rect.bottom + padding),
  };
  r.width = r.right - r.left;
  r.height = r.bottom - r.top;

  // ─── Tooltip placement ──────────────────────────────────
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tipWidth = Math.min(320, vw - 32);
  const tipMargin = 14;
  // Estimate tooltip height for placement logic — actual height
  // depends on body length, but a 180px reservation is enough for
  // 2-line titles + 3 lines of body + buttons.
  const tipHeightEstimate = 220;

  let tipPlacement = placement;
  if (placement === "auto") {
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;
    tipPlacement = spaceBelow >= tipHeightEstimate + tipMargin ? "bottom"
                 : spaceAbove >= tipHeightEstimate + tipMargin ? "top"
                 : (spaceBelow >= spaceAbove ? "bottom" : "top");
  }

  const tipTop = tipPlacement === "bottom"
    ? r.bottom + tipMargin
    : "auto";
  const tipBottom = tipPlacement === "top"
    ? (vh - r.top + tipMargin)
    : "auto";

  // Horizontal centering on the target, constrained to viewport.
  let tipLeft = (rect.left + rect.width / 2) - tipWidth / 2;
  tipLeft = Math.max(12, Math.min(tipLeft, vw - tipWidth - 12));

  // Arrow pointing at the target — small triangle on the side
  // facing the target.
  const arrowSize = 8;
  const arrowLeft = Math.max(
    20,
    Math.min(
      tipWidth - 20,
      (rect.left + rect.width / 2) - tipLeft
    )
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="coach-title"
      aria-describedby="coach-body"
      style={{ position: "fixed", inset: 0, zIndex: 9998, pointerEvents: "none" }}
    >
      <style>{`
        @keyframes coach-pulse {
          0%, 100% { box-shadow: 0 0 0 0 var(--ac, #3b82f6); }
          50%      { box-shadow: 0 0 0 8px transparent; }
        }
        @keyframes coach-tooltip-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .coach-anim { animation: none !important; }
        }
      `}</style>

      {/* 4-div spotlight dim — leaves the target rect untouched.
          pointer-events all dim divs so they catch clicks (so an
          accidental tap on the dim area doesn't navigate). The
          target itself stays clickable because no div covers it. */}
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: r.top, background: bg, pointerEvents: "auto" }} aria-hidden="true" />
      <div style={{ position: "fixed", top: r.bottom, left: 0, right: 0, bottom: 0, background: bg, pointerEvents: "auto" }} aria-hidden="true" />
      <div style={{ position: "fixed", top: r.top, left: 0, width: r.left, height: r.height, background: bg, pointerEvents: "auto" }} aria-hidden="true" />
      <div style={{ position: "fixed", top: r.top, left: r.right, right: 0, height: r.height, background: bg, pointerEvents: "auto" }} aria-hidden="true" />

      {/* Pulsing accent ring around the target. Sits inside the
          spotlight so it draws attention without blocking taps. */}
      <div
        className="coach-anim"
        aria-hidden="true"
        style={{
          position: "fixed",
          top: r.top, left: r.left,
          width: r.width, height: r.height,
          borderRadius: 10,
          border: `2px solid ${ac}`,
          boxShadow: `0 0 0 4px ${ac}33`,
          pointerEvents: "none",
          "--ac": ac,
        }}
      />

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="coach-anim"
        style={{
          position: "fixed",
          top: tipTop, bottom: tipBottom, left: tipLeft,
          width: tipWidth,
          background: cardBg,
          borderRadius: 14,
          padding: "16px 18px 14px",
          boxShadow: "0 14px 40px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.06)",
          border: `1px solid ${bd}`,
          animation: "coach-tooltip-in 0.22s cubic-bezier(0.16, 1, 0.3, 1)",
          pointerEvents: "auto",
        }}
      >
        {/* Arrow caret */}
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: arrowLeft - arrowSize,
            [tipPlacement === "bottom" ? "top" : "bottom"]: -arrowSize,
            width: 0, height: 0,
            borderLeft: `${arrowSize}px solid transparent`,
            borderRight: `${arrowSize}px solid transparent`,
            [tipPlacement === "bottom" ? "borderBottom" : "borderTop"]: `${arrowSize}px solid ${cardBg}`,
            filter: "drop-shadow(0 -1px 1px rgba(0,0,0,0.08))",
          }}
        />

        {/* Skip — top-right */}
        <button
          onClick={onSkip}
          aria-label="Skip onboarding"
          style={{
            position: "absolute", top: 8, right: 8,
            background: "transparent", border: "none",
            color: sc, fontSize: 11, fontWeight: 600,
            cursor: "pointer", padding: "6px 8px", borderRadius: 6,
          }}
        >
          Skip
        </button>

        {stepLabel && (
          <div style={{
            color: ac,
            fontSize: 10, fontWeight: 700,
            letterSpacing: "0.6px", textTransform: "uppercase",
            marginBottom: 4,
          }}>
            {stepLabel}
          </div>
        )}

        <h3
          id="coach-title"
          style={{
            color: tx, fontSize: 15, fontWeight: 700,
            margin: "0 0 6px", letterSpacing: "-0.2px", lineHeight: 1.3,
            paddingRight: 36, // room for the Skip button
          }}
        >
          {title}
        </h3>

        <p
          id="coach-body"
          style={{
            color: sc, fontSize: 12.5, lineHeight: 1.5,
            margin: "0 0 14px",
          }}
        >
          {body}
        </p>

        <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
          {!isFirst && (
            <button
              onClick={onBack}
              aria-label="Previous step"
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: `1px solid ${bd}`,
                background: "transparent",
                color: tx,
                fontWeight: 600, fontSize: 12,
                cursor: "pointer",
                minHeight: 36,
              }}
            >
              ‹
            </button>
          )}
          <button
            ref={primaryBtnRef}
            onClick={onNext}
            style={{
              flex: 1,
              padding: "10px 14px",
              borderRadius: 10,
              border: "none",
              background: ac,
              color: "#fff",
              fontWeight: 700, fontSize: 12.5,
              letterSpacing: "0.1px",
              cursor: "pointer",
              boxShadow: `0 4px 12px ${ac}50`,
              minHeight: 36,
            }}
          >
            {isLast ? "Finish" : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
}
